/**
 * Shared Card Media Component
 *
 * Renders the image section of a result card with Pexels URL optimization,
 * Pexels attribution badge, lazy loading, and a placeholder fallback.
 */

import { h } from 'preact';
import htm from 'htm';
import { IconPhoto } from '../../../../components/Icons.js';
import { asText, pickText } from './fields.js';

const html = htm.bind(h);

/**
 * Optimizes image URLs from Pexels by setting maximum dimensions and compression.
 *
 * @param {string} url - The original image URL.
 * @returns {string} The optimized image URL.
 */
function optimizeImageUrl(url) {
  if (!url) return '';
  if (url.includes('images.pexels.com')) {
    try {
      const urlObj = new URL(url);
      urlObj.searchParams.set('w', '800');
      urlObj.searchParams.set('fit', 'crop');
      urlObj.searchParams.set('auto', 'compress,format');
      urlObj.searchParams.set('q', '75');
      return urlObj.toString();
    } catch (e) {
      return url;
    }
  }
  return url;
}

/**
 * Card media section with hero image, Pexels attribution, and placeholder fallback.
 *
 * @param {Object} props
 * @param {Object} props.item - The resource item data.
 * @param {Object} props.block - Parent block data (used as image fallback).
 * @param {Object} props.ctx - View context containing `t` (i18n helper).
 * @param {string} [props.variant] - Visual variant (e.g., 'popup').
 * @param {Function} [props.onClose] - Callback when the close button is clicked.
 */
export function CardMedia({ item, block, ctx, variant, onClose }) {
  // `photos` may arrive as an array or as a single URL string.
  const rawImageUrl =
    asText(item?.image_url) ||
    pickText(item, 'photos') ||
    asText(block?.image_url) ||
    asText(block?.cover_image_url);
  const imageUrl = optimizeImageUrl(rawImageUrl);
  const altText = pickText(item, 'name', 'label', 'title');

  if (imageUrl) {
    const wrapClass = variant === 'popup' ? 'mta-map-popup-img-wrap' : 'mta-card-image-wrap';
    const imgClass = variant === 'popup' ? 'mta-map-popup-img' : 'mta-card-image';

    return html`
      <div class="${wrapClass}">
        <img src="${imageUrl}" alt="${altText}" class="${imgClass}" loading="lazy" />
        ${variant === 'popup' && html`
          <button type="button" class="mta-map-popup-close-btn" aria-label="Close" onClick=${onClose}>✕</button>
        `}
        ${imageUrl.includes('images.pexels.com') && html`
          <a href="https://www.pexels.com" target="_blank" rel="noopener noreferrer" class="mta-pexels-attribution">
            ${ctx?.t ? ctx.t('photos_by_pexels') : 'Photos by Pexels'}
          </a>
        `}
      </div>
    `;
  }

  if (variant === 'popup') {
    return html`
      <div class="mta-map-popup-close-wrap">
        <button type="button" class="mta-map-popup-close-btn" aria-label="Close" onClick=${onClose}>✕</button>
      </div>
    `;
  }

  return html`
    <div class="mta-card-image-placeholder">
      <${IconPhoto} />
    </div>
  `;
}

